import { useState, useRef } from 'react';

const AUDIT_OPTIONS = [
  { id: 'usfda', label: 'USFDA', description: 'U.S. Food & Drug Administration' },
  { id: 'health-canada', label: 'Santé Canada', description: 'BPF Santé Canada' },
  { id: 'edqm', label: 'EDQM', description: 'Direction européenne de la qualité du médicament' },
  { id: 'mhra', label: 'MHRA', description: 'Medicines & Healthcare products Regulatory Agency' },
  { id: 'who', label: 'OMS', description: 'BPF Organisation mondiale de la Santé' },
  { id: 'pics', label: 'PIC/S', description: 'Pharmaceutical Inspection Co-operation Scheme' },
  { id: 'other', label: 'Autre', description: 'Autre organisme réglementaire reconnu' },
];

type FormStatus = 'idle' | 'submitting' | 'success' | 'error';

export default function SupplierApplicationFormFr() {
  const [selectedAudits, setSelectedAudits] = useState<string[]>([]);
  const [auditDropdownOpen, setAuditDropdownOpen] = useState(false);
  const [auditError, setAuditError] = useState(false);
  const [status, setStatus] = useState<FormStatus>('idle');
  const [charCount, setCharCount] = useState(0);
  const [honeypot, setHoneypot] = useState('');
  const formRef = useRef<HTMLFormElement>(null);

  const toggleAudit = (id: string) => {
    setSelectedAudits((prev) => {
      const next = prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id];
      if (next.length > 0) setAuditError(false);
      return next;
    });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (status === 'submitting') return;
    if (honeypot) return;

    if (selectedAudits.length === 0) {
      setAuditError(true);
      const auditEl = document.getElementById('audit-dropdown-fr');
      auditEl?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setAuditError(false);

    const form = e.currentTarget;
    const messageField = form.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
    if (messageField && messageField.value.length > 500) return;

    // Normalize website URL
    const websiteEl = form.querySelector<HTMLInputElement>('[name="website"]');
    if (websiteEl && websiteEl.value && !websiteEl.value.match(/^https?:\/\//)) {
      websiteEl.value = `https://${websiteEl.value}`;
    }

    setStatus('submitting');

    const data = new URLSearchParams();
    const fields = ['company_name', 'contact_name', 'email', 'phone', 'country', 'product_categories', 'website', 'product_list_link', 'message'];
    fields.forEach((field) => {
      const el = form.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${field}"]`);
      if (el) data.append(field, el.value);
    });

    const auditLabels = selectedAudits.map(
      (id) => AUDIT_OPTIONS.find((o) => o.id === id)?.label ?? id
    );
    data.append('regulatory_audits', auditLabels.join(', ') || 'Aucune sélection');

    try {
      const res = await fetch('https://readdy.ai/api/form/d7a249b86jhav3jpftng', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        formRef.current?.reset();
        setSelectedAudits([]);
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  const auditSummary =
    selectedAudits.length === 0
      ? 'Sélectionnez tout ce qui s\'applique...'
      : selectedAudits
          .map((id) => AUDIT_OPTIONS.find((o) => o.id === id)?.label)
          .join(', ');

  return (
    <section className="w-full px-6 md:px-16 py-16 bg-stone-50">
      <div className="max-w-5xl mx-auto">
        <div className="mb-12 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div>
            <span className="text-emerald-700 text-xs font-bold uppercase tracking-widest block mb-3">
              Partenariat
            </span>
            <h2
              className="text-3xl md:text-4xl font-extrabold text-stone-900 leading-tight"
              style={{ fontFamily: "'DM Serif Display', serif" }}
            >
              Devenez fournisseur
            </h2>
            <p className="text-stone-500 text-sm mt-3 max-w-lg leading-relaxed">
              Nous collaborons avec des fabricants qualifiés et conformes dans le monde entier. Remplissez le formulaire ci-dessous et notre équipe examinera votre candidature — en français ou en anglais.
            </p>
          </div>
        </div>

        {/* Steps */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-12">
          {[
            { icon: 'ri-file-text-line', step: '01', title: 'Soumettre la candidature', desc: 'Remplissez les informations de votre entreprise et téléchargez votre liste de produits' },
            { icon: 'ri-search-eye-line', step: '02', title: 'Examen et qualification', desc: 'Notre équipe examine votre conformité réglementaire et votre gamme de produits' },
            { icon: 'ri-shake-hands-line', step: '03', title: 'Accord de partenariat', desc: 'Appel d\'intégration et accord de partenariat formel en FR ou EN' },
          ].map(({ icon, step, title, desc }) => (
            <div key={step} className="bg-white rounded-lg p-5 border border-stone-100">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-9 h-9 flex items-center justify-center rounded-full bg-emerald-50">
                  <i className={`${icon} text-emerald-700 text-base`} />
                </div>
                <span className="text-xs font-bold text-stone-300 tracking-widest">{step}</span>
              </div>
              <h4 className="text-sm font-bold text-stone-800 mb-1">{title}</h4>
              <p className="text-xs text-stone-500 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>

        {status === 'success' ? (
          <div className="bg-white rounded-lg border border-emerald-200 p-12 text-center">
            <div className="w-16 h-16 flex items-center justify-center rounded-full bg-emerald-100 mx-auto mb-5">
              <i className="ri-check-line text-emerald-700 text-3xl" />
            </div>
            <h3 className="text-xl font-bold text-stone-900 mb-2" style={{ fontFamily: "'DM Serif Display', serif" }}>
              Candidature reçue
            </h3>
            <p className="text-stone-500 text-sm max-w-md mx-auto">
              Merci de votre intérêt. Notre équipe examinera votre candidature et vous contactera dans 3 à 5 jours ouvrables — dans votre langue préférée.
            </p>
            <button
              onClick={() => setStatus('idle')}
              className="mt-6 text-sm font-semibold text-emerald-700 hover:underline cursor-pointer"
            >
              Soumettre une autre candidature
            </button>
          </div>
        ) : (
          <form
            ref={formRef}
            data-readdy-form
            id="supplier-application-form-fr"
            onSubmit={handleSubmit}
            className="bg-white rounded-lg border border-stone-100 p-8 md:p-10"
          >
            {/* Honeypot — invisible to real users */}
            <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }} aria-hidden="true">
              <input
                type="text"
                name="full_name_confirm"
                value={honeypot}
                onChange={(e) => setHoneypot(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
              />
            </div>
            <h3 className="text-base font-bold text-stone-800 mb-6 pb-4 border-b border-stone-100">
              Informations sur l&apos;entreprise
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-5">
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Nom de l&apos;entreprise <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="company_name"
                  required
                  placeholder="ex. PharmaCorp Ltée"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Nom du contact <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="contact_name"
                  required
                  placeholder="Nom complet"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Adresse courriel <span className="text-red-400">*</span>
                </label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder="contact@entreprise.com"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Numéro de téléphone
                </label>
                <input
                  type="tel"
                  name="phone"
                  placeholder="+1 (555) 000-0000"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Pays / Région <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="country"
                  required
                  placeholder="ex. Inde, Allemagne, Canada"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Catégories de produits
                </label>
                <input
                  type="text"
                  name="product_categories"
                  placeholder="ex. IPA, Excipients, Formes finies"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                  Site web de l&apos;entreprise <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  name="website"
                  required
                  placeholder="https://www.votreentreprise.com"
                  className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
            </div>

            {/* Regulatory Audits */}
            <div className="mb-5" id="audit-dropdown-fr">
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                Audits réglementaires des installations <span className="text-red-400">*</span>
                <span className="text-stone-400 font-normal ml-1">(sélectionnez tout ce qui s&apos;applique)</span>
              </label>
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setAuditDropdownOpen((o) => !o)}
                  className={`w-full flex items-center justify-between px-3 py-2.5 text-sm border rounded-md bg-stone-50 focus:outline-none cursor-pointer text-left transition-colors ${auditError ? 'border-red-400' : 'border-stone-200 focus:border-emerald-400'}`}
                >
                  <span className={selectedAudits.length === 0 ? 'text-stone-400' : 'text-stone-800'}>
                    {auditSummary}
                  </span>
                  <i className={`ri-arrow-down-s-line text-stone-400 transition-transform ${auditDropdownOpen ? 'rotate-180' : ''}`} />
                </button>

                {auditDropdownOpen && (
                  <div className="absolute z-40 top-full left-0 right-0 mt-1 bg-white border border-stone-200 rounded-md overflow-hidden">
                    {AUDIT_OPTIONS.map((opt) => (
                      <button
                        key={opt.id}
                        type="button"
                        onClick={() => toggleAudit(opt.id)}
                        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-stone-50 cursor-pointer transition-colors text-left border-b border-stone-50 last:border-0"
                      >
                        <div className={`w-4 h-4 flex items-center justify-center rounded border flex-shrink-0 transition-colors ${
                          selectedAudits.includes(opt.id)
                            ? 'bg-emerald-700 border-emerald-700'
                            : 'border-stone-300 bg-white'
                        }`}>
                          {selectedAudits.includes(opt.id) && (
                            <i className="ri-check-line text-white text-xs" />
                          )}
                        </div>
                        <div>
                          <span className="text-sm font-semibold text-stone-800">{opt.label}</span>
                          <span className="text-xs text-stone-400 ml-2">{opt.description}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {auditError && (
                <p className="text-xs text-red-500 mt-1.5">Veuillez sélectionner au moins un audit réglementaire.</p>
              )}

              {selectedAudits.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {selectedAudits.map((id) => {
                    const opt = AUDIT_OPTIONS.find((o) => o.id === id);
                    return (
                      <span
                        key={id}
                        className="inline-flex items-center gap-1 text-xs font-semibold bg-emerald-50 text-emerald-700 px-2.5 py-1 rounded-full"
                      >
                        {opt?.label}
                        <button
                          type="button"
                          onClick={() => toggleAudit(id)}
                          className="cursor-pointer hover:text-emerald-900"
                        >
                          <i className="ri-close-line text-xs" />
                        </button>
                      </span>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Product List Link */}
            <div className="mb-5">
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                Lien vers la liste de produits
                <span className="text-stone-400 font-normal ml-1">(Google Drive, Dropbox, WeTransfer, etc.)</span>
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                  <i className="ri-links-line text-stone-400 text-sm" />
                </div>
                <input
                  type="text"
                  name="product_list_link"
                  placeholder="https://drive.google.com/..."
                  className="w-full pl-9 pr-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50"
                />
              </div>
              <p className="text-xs text-stone-400 mt-1.5">
                Partagez un lien vers votre liste de produits hébergée sur n&apos;importe quelle plateforme de stockage en nuage. Assurez-vous que le lien est configuré sur &quot;Toute personne disposant du lien peut consulter&quot;.
              </p>
            </div>

            {/* Message */}
            <div className="mb-8">
              <label className="block text-xs font-semibold text-stone-600 mb-1.5">
                Informations supplémentaires
              </label>
              <textarea
                name="message"
                rows={4}
                maxLength={500}
                placeholder="Parlez-nous de vos capacités de fabrication, de votre capacité, des principaux marchés desservis ou de tout autre détail pertinent..."
                onChange={(e) => setCharCount(e.target.value.length)}
                className="w-full px-3 py-2.5 text-sm border border-stone-200 rounded-md focus:outline-none focus:border-emerald-400 bg-stone-50 resize-none"
              />
              <p className={`text-xs mt-1 text-right ${charCount > 480 ? 'text-red-400' : 'text-stone-400'}`}>
                {charCount}/500
              </p>
            </div>

            {status === 'error' && (
              <p className="text-sm text-red-500 mb-4">
                Une erreur s&apos;est produite. Veuillez réessayer ou nous contacter directement.
              </p>
            )}

            {/* Patent Note */}
            <div className="mb-6 bg-amber-50 border border-amber-200 rounded-md px-4 py-3">
              <p className="text-xs text-amber-800 leading-relaxed">
                <span className="font-bold uppercase tracking-wide">Note :</span> Nous ne sommes pas en mesure de distribuer des produits activement couverts par des brevets de procédé ou de produit, et il est de la responsabilité du fabricant de s&apos;assurer que les produits ne violent pas les brevets actifs sur les marchés identifiés.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <p className="text-xs text-stone-400 max-w-sm">
                Nous répondons en français ou en anglais dans 3 à 5 jours ouvrables. Vos informations sont strictement confidentielles.
              </p>
              <button
                type="submit"
                disabled={status === 'submitting'}
                className="whitespace-nowrap bg-emerald-700 text-white text-sm font-bold px-8 py-3 rounded-md hover:bg-emerald-800 transition-colors cursor-pointer disabled:opacity-60"
              >
                {status === 'submitting' ? 'Envoi en cours...' : 'Soumettre la candidature'}
              </button>
            </div>
          </form>
        )}
      </div>
    </section>
  );
}
